import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, UserPlus, LogIn, ArrowLeft } from 'lucide-react';

const LandingPage = () => {
  const { user, login, register, forgotPassword, loading } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('welcome'); // welcome | login | register | forgot
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');

  useEffect(() => {
    if (user && !loading) navigate('/dashboard');
  }, [user, loading, navigate]);

  const resetForm = () => {
    setEmail(''); setPassword(''); setConfirmPassword(''); setName('');
    setShowPassword(false); setShowConfirmPassword(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error('Preencha todos os campos');
    setSubmitting(true);
    try {
      await login(email, password, remember);
      toast.success('Login realizado!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fazer login');
    } finally { setSubmitting(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!name || !email || !password || !confirmPassword) return toast.error('Preencha todos os campos');
    if (password.length < 6) return toast.error('Senha deve ter pelo menos 6 caracteres');
    if (password !== confirmPassword) return toast.error('As senhas não coincidem');
    setSubmitting(true);
    try {
      const res = await register(name, email, password);
      toast.success(res.message);
      resetForm();
      setMode('login');
    } catch (error) {
      // Show the real backend error to the admin — helps diagnose weird cases
      // (e.g. lingering unique-key violations after a user deletion).
      const raw = error.response?.data?.detail;
      let msg = 'Erro ao criar conta';
      if (typeof raw === 'string') msg = raw;
      else if (Array.isArray(raw) && raw[0]?.msg) msg = raw[0].msg;
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    const trimmed = (email || '').trim();
    if (!trimmed) return toast.error('Informe seu email');
    // Client-side sanity check — the backend now tolerates malformed emails
    // silently (returns the neutral message anyway), but we still warn users
    // so they don't accidentally submit a typo like "joao@gmail" (no .com).
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      return toast.error('Email parece estar incompleto. Confira o domínio (ex: @gmail.com).');
    }
    setSubmitting(true);
    try {
      const res = await forgotPassword(trimmed);
      toast.success(res.message);
      resetForm();
      setMode('login');
    } catch (error) {
      // Pydantic 422 returns detail as an array [{msg, loc, ...}]. Extract a
      // human-readable string, otherwise fall back to a generic message.
      const raw = error.response?.data?.detail;
      let msg = 'Erro ao enviar email. Verifique se o endereço está correto.';
      if (typeof raw === 'string') msg = raw;
      else if (Array.isArray(raw) && raw[0]?.msg) msg = raw[0].msg;
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900 flex flex-col items-center justify-center p-6">
      {/* Logo */}
      <div className="mb-8">
        <div className="bg-white rounded-2xl p-6 shadow-2xl shadow-teal-500/10">
          <img
            src="https://customer-assets.emergentagent.com/job_dubsync-3/artifacts/jj7xe7zi_Screenshot_3.jpeg.png"
            alt="DUBLEIROS STUDIOS"
            className="w-auto h-20 md:h-28 object-contain"
            data-testid="dubleiros-logo"
          />
        </div>
      </div>

      {/* Auth Card */}
      <div className="w-full max-w-md" data-testid="auth-card">

        {/* WELCOME */}
        {mode === 'welcome' && (
          <div className="text-center space-y-6">
            <div>
              <h1 className="text-3xl font-bold text-white">Bem-vindo ao <span className="text-teal-400">DUBLEIROS</span></h1>
              <p className="text-slate-400 mt-2">Plataforma de gestão interna para dubladores profissionais</p>
            </div>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => { resetForm(); setMode('login'); }}
                className="w-full bg-teal-500 hover:bg-teal-600 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
                data-testid="welcome-login-btn"
              >
                <LogIn className="h-5 w-5" />
                Fazer Login
              </button>
              <button
                onClick={() => { resetForm(); setMode('register'); }}
                className="w-full bg-slate-700/60 hover:bg-slate-700 border border-slate-600 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
                data-testid="welcome-register-btn"
              >
                <UserPlus className="h-5 w-5" />
                Cadastrar
              </button>
            </div>
          </div>
        )}

        {/* LOGIN */}
        {mode === 'login' && (
          <div className="bg-slate-800/60 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-xl">
          <form onSubmit={handleLogin} className="space-y-5">
            <div className="text-center mb-2">
              <h2 className="text-2xl font-bold text-white">Entrar</h2>
              <p className="text-slate-400 text-sm mt-1">Acesse sua conta</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <input
                  type="email" value={email} onChange={e => setEmail(e.target.value)}
                  className="w-full bg-slate-700/50 border border-slate-600 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/30"
                  placeholder="seu@email.com" autoComplete="email"
                  data-testid="login-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Senha</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <input
                  type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                  className="w-full bg-slate-700/50 border border-slate-600 rounded-lg pl-10 pr-10 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/30"
                  placeholder="Sua senha" autoComplete="current-password"
                  data-testid="login-password"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="remember-checkbox">
                <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-teal-500 focus:ring-teal-500/30" />
                <span className="text-sm text-slate-400">Lembrar acesso</span>
              </label>
              <button type="button" onClick={() => { resetForm(); setMode('forgot'); }}
                className="text-sm text-teal-400 hover:text-teal-300 transition-colors"
                data-testid="forgot-password-link"
              >
                Esqueci minha senha
              </button>
            </div>

            <button type="submit" disabled={submitting}
              className="w-full bg-teal-500 hover:bg-teal-600 disabled:bg-teal-500/50 text-white font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
              data-testid="login-submit"
            >
              {submitting ? <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white" /> : <LogIn className="h-5 w-5" />}
              {submitting ? 'Entrando...' : 'Entrar'}
            </button>

            <p className="text-center text-sm text-slate-400">
              Não tem conta?{' '}
              <button type="button" onClick={() => { resetForm(); setMode('register'); }}
                className="text-teal-400 hover:text-teal-300 font-medium transition-colors"
                data-testid="go-to-register"
              >
                Criar conta
              </button>
            </p>
          </form>
          </div>
        )}

        {/* REGISTER */}
        {mode === 'register' && (
          <div className="bg-slate-800/60 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-xl">
          <form onSubmit={handleRegister} className="space-y-5">
            <div className="text-center mb-2">
              <h2 className="text-2xl font-bold text-white">Criar Conta</h2>
              <p className="text-slate-400 text-sm mt-1">Preencha seus dados</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Nome Completo</label>
              <input
                type="text" value={name} onChange={e => setName(e.target.value)}
                className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/30"
                placeholder="Seu nome" autoComplete="name"
                data-testid="register-name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <input
                  type="email" value={email} onChange={e => setEmail(e.target.value)}
                  className="w-full bg-slate-700/50 border border-slate-600 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/30"
                  placeholder="seu@email.com" autoComplete="email"
                  data-testid="register-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Senha</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <input
                  type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                  className="w-full bg-slate-700/50 border border-slate-600 rounded-lg pl-10 pr-10 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/30"
                  placeholder="Mínimo 6 caracteres" autoComplete="new-password"
                  data-testid="register-password"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Confirmar Senha</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                  className="w-full bg-slate-700/50 border border-slate-600 rounded-lg pl-10 pr-10 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/30"
                  placeholder="Repita a senha" autoComplete="new-password"
                  data-testid="register-confirm-password"
                />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={submitting}
              className="w-full bg-teal-500 hover:bg-teal-600 disabled:bg-teal-500/50 text-white font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
              data-testid="register-submit"
            >
              {submitting ? <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white" /> : <UserPlus className="h-5 w-5" />}
              {submitting ? 'Criando...' : 'Criar Conta'}
            </button>

            <p className="text-center text-sm text-slate-400">
              Já tem conta?{' '}
              <button type="button" onClick={() => { resetForm(); setMode('login'); }}
                className="text-teal-400 hover:text-teal-300 font-medium transition-colors"
                data-testid="go-to-login"
              >
                Fazer login
              </button>
            </p>
          </form>
          </div>
        )}

        {/* FORGOT PASSWORD */}
        {mode === 'forgot' && (
          <div className="bg-slate-800/60 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-xl">
          <form onSubmit={handleForgot} className="space-y-5">
            <div className="text-center mb-2">
              <h2 className="text-2xl font-bold text-white">Esqueci Minha Senha</h2>
              <p className="text-slate-400 text-sm mt-1">Informe seu email para receber o link de redefinição</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <input
                  type="email" value={email} onChange={e => setEmail(e.target.value)}
                  className="w-full bg-slate-700/50 border border-slate-600 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/30"
                  placeholder="seu@email.com" autoComplete="email"
                  data-testid="forgot-email"
                />
              </div>
            </div>

            <button type="submit" disabled={submitting}
              className="w-full bg-teal-500 hover:bg-teal-600 disabled:bg-teal-500/50 text-white font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
              data-testid="forgot-submit"
            >
              {submitting ? <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white" /> : <Mail className="h-5 w-5" />}
              {submitting ? 'Enviando...' : 'Enviar Link de Redefinição'}
            </button>

            <button type="button" onClick={() => { resetForm(); setMode('login'); }}
              className="w-full flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-white transition-colors py-2"
              data-testid="back-to-login"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar ao login
            </button>
          </form>
          </div>
        )}
      </div>

      <p className="text-slate-500 text-sm mt-8">
        &copy; 2025 DUBLEIROS STUDIOS. Todos os direitos reservados.
      </p>
    </div>
  );
};

export default LandingPage;
